import { booleanTransformer, stringTransformer, useRouteQuery } from 'vue-use-route-query';
import type { Product } from '~/types/search';
import type { FilterBySubQuery } from '~/types/typesense';

export const useFilters = () => {
  const route = useRoute();

  const sort = useRouteQuery(
    'sort',
    PRODUCT_SORTS[0]!.value,
    sortTransformer(PRODUCT_SORTS),
    { mode: 'push' },
  );

  const search = useRouteQuery('q', null, { mode: 'push' });

  const page = useRouteQuery('page', 1, defaultIntegerTransformer(1), { mode: 'push' });

  const size = useRouteQuery('size', 48, defaultIntegerTransformer(48), { mode: 'push' });

  const filters = {
    availability: new ProductDataFilter('availability', null, availabilityTransformer, availabilityMaterializer),
    model: new ProductDataFilter('model', null, stringTransformer, value => ({ model: { $eq: value } })),
    condition: new ProductDataFilter('condition', [], stringArrayTransformer(), value => ({ condition: { $in: value } })),
    caliber: new ProductDataFilter('caliber', [], stringArrayTransformer(), value => ({ caliber: { $in: value } })),
    category: new ProductDataFilter('category', [], stringArrayTransformer(), value => ({ categories: { slug: { $in: value } } })),
    action: new ProductDataFilter('action', [], stringArrayTransformer(), value => ({ action: { $in: value } })),
    finish: new ProductDataFilter('finish', [], stringArrayTransformer(), value => ({ finish: { $in: value } })),
    hand: new ProductDataFilter('hand', [], stringArrayTransformer(), value => ({ hand: { $in: value } })),
    brand: new ProductDataFilter('brand', [], stringArrayTransformer(), value => ({ manufacturer: { slug: { $in: value } } })),
    capacity: new ProductDataFilter('capacity', null, numericRangeTransformer(), value => ({ capacity: { $gte: value.min, $lte: value.max } })),
    barrel_length: new ProductDataFilter('barrel_length', null, numericRangeTransformer(), value => ({ barrelLength: { $gte: value.min, $lte: value.max } })),
    price_range: new ProductDataFilter('price_range', null, stringArrayTransformer(), value => ({ priceFacet: { value: { $in: value } } })),
    firearms: new ProductDataFilter('firearms', null, booleanTransformer, value => ({ isFirearm: { $eq: value } })),
    qp: new ProductDataFilter('qp', null, booleanTransformer, value => ({ isQualifiedProfessional: { $eq: value } })),
    sale: new ProductDataFilter('sale', null, booleanTransformer, value => ({ isOnSale: { $eq: value } })),
    rebate: new ProductDataFilter('rebate', null, booleanTransformer, value => ({ hasRebates: { $eq: value } })),
  };

  const active = computed(() => Object.values(filters).filter(f => f.active.value));

  const inactive = computed(() => Object.values(filters).filter(f => !f.active.value));

  const queries
    = computed(() => active.value.map(f => f.query.value as FilterBySubQuery<Product>));

  const reset = () => {
    navigateTo({
      path: route.path,
      query: {
        sort: route.query.sort,
        size: route.query.size,
        page: route.query.page,
        q: route.query.q,
      },
    });
  };

  return {
    filters,
    active,
    inactive,
    queries,
    sort,
    search,
    page,
    size,
    reset,
  };
};
